<template>
  <div class="flex flex-col min-h-full divide-y">
    <div class="px-6 py-4 bg-white">
      <span class="uppercase text-xs font-semibold tracking-wider text-gray-600">Order Item</span>
      <h6>
        {{ orderItem.name }}
      </h6>
    </div>
    <div class="flex-1 bg-white divide-y">
      <div v-if="orderItem.requirements" class="divide-y">
        <div class="px-6 py-4 flex justify-between items-center">
          <h5 class="uppercase font-semibold text-base text-gray-800">Requirements</h5>
          <BaseButton
            ghost
            small
            class="px-3"
            :disabled="canCopy === false"
            @click="copyRequirements"
          >
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              class="inline-flex w-5 h-5 mr-2"
            >
              <path
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy</span>
          </BaseButton>
        </div>
        <div class="px-6 py-4">
          <pre class="whitespace-pre-wrap break-words" v-text="orderItem.requirements" />
        </div>
      </div>
      <div v-if="links.length" class="divide-y">
        <div class="px-6 py-4 flex justify-between items-center">
          <h5 class="uppercase font-semibold text-base text-gray-800">Links</h5>
          <BaseButton
            ghost
            small
            class="px-3"
            :disabled="canCopy === false"
            @click="copyLinks(links)"
          >
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              class="inline-flex w-5 h-5 mr-2"
            >
              <path
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy All</span>
          </BaseButton>
        </div>

        <ul class="divide-y">
          <li
            v-for="link in links"
            :key="link.id"
            class="px-6 pt-4 pb-6 hover:bg-gray-100 select-none space-y-4"
          >
            <a :href="link.url" target="_blank">
              {{ link.title ? link.title : link.url }}
            </a>
            <p v-if="link.description" class="text-gray-800">
              {{ link.description }}
            </p>
            <div>
              <BaseButton
                secondary
                outline
                small
                class="px-3"
                :disabled="canCopy === false"
                @click="copyLink(link)"
              >
                <svg
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  class="inline-flex w-5 h-5 mr-2"
                >
                  <path
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                    12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>Copy</span>
              </BaseButton>
            </div>
          </li>
        </ul>
      </div>
      <div v-if="notes.length" class="divide-y">
        <div class="px-6 py-4 flex justify-between items-center">
          <h5 class="uppercase font-semibold text-base text-gray-800">Notes</h5>
          <BaseButton
            ghost
            small
            class="px-3"
            :disabled="canCopy === false"
            @click="copyNotes(notes)"
          >
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              class="inline-flex w-5 h-5 mr-2"
            >
              <path
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy All</span>
          </BaseButton>
        </div>

        <ul class="divide-y">
          <li
            v-for="note in notes"
            :key="note.id"
            class="px-6 pt-4 pb-6 hover:bg-gray-100 select-none space-y-4"
          >
            <p class="text-gray-800">
              {{ note.content }}
            </p>
            <BaseButton
              secondary
              outline
              small
              class="px-3"
              :disabled="canCopy === false"
              @click="copyNote(note)"
            >
              <svg
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                stroke="currentColor"
                viewBox="0 0 24 24"
                class="inline-flex w-5 h-5 mr-2"
              >
                <path
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                  12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span>Copy</span>
            </BaseButton>
          </li>
        </ul>
      </div>
      <div v-if="media.length" class="divide-y">
        <div class="px-6 py-4 flex justify-between items-center">
          <h5 class="uppercase font-semibold text-base text-gray-800">Media</h5>
          <BaseButton
            ghost
            small
            class="px-3"
            :disabled="canCopy === false"
            @click="copyMedia(media)"
          >
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              class="inline-flex w-5 h-5 mr-2"
            >
              <path
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy All</span>
          </BaseButton>
        </div>

        <ul class="divide-y">
          <li
            v-for="medium in media"
            :key="medium.id"
            class="px-6 pt-4 pb-6 hover:bg-gray-100 select-none space-y-4"
          >
            <a :href="medium.url" target="_blank">
              {{ medium.name ? medium.name : medium.url }}
            </a>
            <div>
              <BaseButton
                secondary
                outline
                small
                class="px-3"
                :disabled="canCopy === false"
                @click="copyMedium(medium)"
              >
                <svg
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  class="inline-flex w-5 h-5 mr-2"
                >
                  <path
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
                    12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>Copy</span>
              </BaseButton>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    orderItem: {
      type: Object,
      required: true,
    },
    canCopy: Boolean,
  },

  computed: {
    ...mapState('entities', {
      links({ links: { byId: linksById } }) {
        return this.orderItem.links.map((linkId) => linksById[linkId]);
      },
      notes({ notes: { byId: notesById } }) {
        return this.orderItem.notes.map((noteId) => notesById[noteId]);
      },
      media({ media: { byId: mediaById } }) {
        return this.orderItem.media.map((mediaId) => mediaById[mediaId]);
      },
    }),
  },

  methods: {
    copyRequirements() {
      this.$emit('requirements:copy', this.orderItem.requirements);
    },
    copyLinks(links) {
      const copiedLinks = links.map(({ url, title, description }) => ({ url, title, description }));
      this.$emit('links:copy', copiedLinks);
    },
    copyLink(link) {
      this.copyLinks([link]);
    },
    copyNotes(notes) {
      const copiedNotes = notes.map(({ content }) => ({ content }));
      this.$emit('notes:copy', copiedNotes);
    },
    copyNote(note) {
      this.copyNotes([note]);
    },
    copyMedia(media) {
      const copiedMedia = media.map(({ url, name: title }) => ({ url, title, description: '' }));
      this.$emit('media:copy', copiedMedia);
    },
    copyMedium(medium) {
      this.copyMedia([medium]);
    },
  },
};
</script>
